import { computedFrom } from "aurelia-binding";
import { autoinject, transient } from "aurelia-dependency-injection";
import { Router } from "aurelia-router";
import { bindable } from "aurelia-templating";
import * as moment from "moment";
import { RightShipQiRouterConstants } from "../../../../../../../configuration/rightshipqi-router.constants";
import { Claims } from "../../../../../../../constants/authentication/claims";
import { UserClaimsService } from "../../../../../../../services/authentication/user-claims.service";
import { ClipboardCopyService } from "../../../../../../../utilities/clipboard-copy-service";
import { Reflection } from "../../../../../../../utilities/reflection";
import { type VesselPreArrivalDetailsModel } from "../../../models/ports-and-terminals.models";

@autoinject
@transient()
export class VesselPreArrivalHeader {

    @bindable()
    vesselPreArrivalDetails: VesselPreArrivalDetailsModel;

    @bindable()
    forceLoad: boolean = false;

    @bindable
    showGrading: boolean = false;

    @bindable
    showVesselComparisonLink: boolean = false;

    @bindable
    redirectToVessel: boolean = true;

    canRequestRightShipInspection: boolean;
    canAccessVesselComparison: boolean;

    constructor(private clipboardCopyService: ClipboardCopyService,
        userClaimsService: UserClaimsService, private router: Router) {
        this.canRequestRightShipInspection = userClaimsService.hasPermission(Claims.CanRequestRightShipInspection);
        this.canAccessVesselComparison = userClaimsService.hasPermission(Claims.CanAccessVesselComparison);
    }

    @computedFrom(
        'vesselPreArrivalDetails',
        'vesselPreArrivalDetails.estimatedArrivalTime',
        'vesselPreArrivalDetails.estimatedDepartureTime'
    )
    public get date() {

        if (new Date(this.vesselPreArrivalDetails.estimatedArrivalTime) > new Date()) {
            return moment(this.vesselPreArrivalDetails.estimatedArrivalTime).utc().format("DD MMM YYYY, HH:mm")
        } else if (new Date(this.vesselPreArrivalDetails.estimatedArrivalTime) < new Date() &&
            new Date(this.vesselPreArrivalDetails.estimatedDepartureTime) > new Date()) {
            return moment(this.vesselPreArrivalDetails.estimatedArrivalTime).utc().format("DD MMM YYYY, HH:mm")
        }
        else {
            return moment(this.vesselPreArrivalDetails.estimatedDepartureTime).utc().format("DD MMM YYYY, HH:mm")
        }
    }

    @computedFrom(
        'vesselPreArrivalDetails',
        'vesselPreArrivalDetails.estimatedArrivalTime',
        'vesselPreArrivalDetails.estimatedDepartureTime'
    )
    public get dateLabel() {
        if (new Date(this.vesselPreArrivalDetails.estimatedArrivalTime) > new Date()) {
            return "Estimated Arrival";
        } else if (new Date(this.vesselPreArrivalDetails.estimatedArrivalTime) < new Date() &&
            new Date(this.vesselPreArrivalDetails.estimatedDepartureTime) > new Date()) {
            return "Arrival Date";
        }
        else {
            return "Departed Date";
        }
    }

    @computedFrom('vesselPreArrivalDetails.isSanctioned', 'vesselPreArrivalDetails.isInGeoPoliticalZone', 'vesselPreArrivalDetails.hasGeopoliticalConflict')
    get isSanctionedOrGeopoliticalZone() {
        if (this.vesselPreArrivalDetails.isSanctioned || this.vesselPreArrivalDetails.isInGeoPoliticalZone || this.vesselPreArrivalDetails.hasGeopoliticalConflict) {
            return true;
        }
    }

    @computedFrom(Reflection.getPropertyPath<VesselPreArrivalHeader>(v => v.vesselPreArrivalDetails.vesselId))
    public get vesselUrl() {
        return this.router.generate(RightShipQiRouterConstants.RouteNames.VesselDetails, {
            id: this.vesselPreArrivalDetails.vesselId,
        });
    }

    copy(str: string) {
        this.clipboardCopyService.copy(str);
    }

    navigateToComparisonPage() {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.VesselComparison, { vesselId: this.vesselPreArrivalDetails.vesselId });
    }
}